/**
 * 상회칠판 오늘 칠판 부품 (시안 09 · 08 6장 B1~B4 · MO4 · MO5, D52).
 *
 *   칠판 머리(--board, 날짜 손글씨 36/40 · 요일 소개 22/26) → 적힌 칸(종이 카드, 손글씨 제목 24/26 「HH:MM · 칸 이름」
 *   + 「방금」 + 초상 20 「{멤버}가 적음」) → 오늘 남은 칸(점선 윤곽, 손글씨 22/24) → 지난 칠판(날짜 줄, 펼치면 칸 요약)
 *
 * - 칠판은 머리 영역에만, 칸은 종이 카드 그대로(08 결정 32). 손글씨는 날짜 · 요일 소개 · 칸 제목 · 예정 줄 · 「방금」에만
 * - 동작 줄이기: MO4 = 글자 교차 페이드, MO5 = 즉시 표시(motion-safe 로만 움직임)
 */
import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { getMember } from '@config/members/index.js';
import { withJosa } from '@shared/korean.js';
import { Avatar } from '../../components/Avatar.jsx';
import { DailyItemCard } from './BoardCards.jsx';
import { formatClock24, isJustWritten } from './boardFeed.js';

const EMPTY_BOARD_TEXT = '오늘 칠판은 아직 깨끗해. 아침 7시 반에 이모가 운세부터 적을게.';
const GRAIN = {
    backgroundImage: 'radial-gradient(var(--color-chalk-grain) 0.6px, transparent 0.6px)',
    backgroundSize: '5px 5px',
};

function nameOf(memberKey) {
    return getMember(memberKey)?.name ?? '';
}

/**
 * 칠판 머리. erasing 이면 지우개 띠가 지나가며 옛 날짜를 지웁니다(MO4).
 * 잘리지 않게(SPEC_CHANGE_v1.3 17.7, D71): 고정 높이 없음, 위아래 안쪽 여백 16 이상, 손글씨 줄 높이 1.3 이상(36/48 · 22/30),
 * 판은 내용만큼 늘어나고 자르지 않습니다(지우개 띠만 따로 겹에서 잘라 냄). 긴 날짜는 띄어쓰기에서 줄을 바꿉니다
 */
export function ChalkHeader({ dateLabel, weekdayLine, calendarLine = null, shopLine = null, empty, erasing = null }) {
    return (
        <section
            aria-label={[dateLabel, weekdayLine, calendarLine].filter(Boolean).join(', ')}
            className="relative mt-3 rounded-card bg-board px-[18px] pt-4 pb-5"
            style={GRAIN}
        >
            {erasing ? (
                <span aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden rounded-card">
                    <span
                        className="absolute inset-y-0 left-0 w-[70%] blur-[6px] motion-safe:animate-board-erase-band"
                        style={{
                            background:
                                'linear-gradient(90deg, transparent, rgba(244,241,232,0.16) 40%, rgba(244,241,232,0.22) 60%, transparent)',
                        }}
                    />
                    <span className="absolute top-4 right-[18px] left-[18px] font-hand text-[36px] leading-[48px] break-keep text-chalk motion-safe:animate-board-erase-text motion-reduce:opacity-0">
                        {erasing}
                    </span>
                </span>
            ) : null}
            <h2
                role="heading"
                aria-level={2}
                className={`m-0 font-hand text-[36px] leading-[48px] font-normal break-keep text-chalk ${
                    erasing ? 'motion-safe:animate-chalk-write motion-safe:[animation-delay:400ms]' : ''
                }`}
            >
                {dateLabel}
            </h2>
            <p className="m-0 font-hand text-[22px] leading-[30px] break-keep text-chalk-dim">{weekdayLine}</p>
            {/* 절기 · 공휴일(서버 달력 파일 값만, D77) */}
            {calendarLine ? (
                <p className="m-0 font-hand text-[19px] leading-[26px] break-keep text-chalk-dim">{calendarLine}</p>
            ) : null}
            {empty ? <p className="m-0 mt-3 text-[15px] leading-6 text-chalk">{EMPTY_BOARD_TEXT}</p> : null}
            {/* 이모 가게 짧은 문구(날짜로 번갈아, D96) */}
            {shopLine ? <p className="m-0 mt-2 text-[13px] leading-5 break-keep text-chalk-dim">{shopLine}</p> : null}
        </section>
    );
}

/** 분필 밑줄 「방금」 */
function JustNow() {
    return (
        <span className="relative inline-flex flex-col items-start">
            <span className="font-hand text-[20px] leading-[22px] text-board">방금</span>
            <svg width="40" height="6" viewBox="0 0 40 6" aria-hidden="true" className="-mt-0.5">
                <path
                    d="M1 4c8-2 16-2.5 24-1.5S36 4 39 3"
                    fill="none"
                    stroke="var(--color-board)"
                    strokeOpacity="0.55"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeDasharray="44"
                    className="motion-safe:animate-chalk-underline"
                />
            </svg>
        </span>
    );
}

/** 칸 제목 줄: 「07:30 · 오늘의 운세」 + 「방금」 + 오른쪽 초상 20 「이모가 적음」 */
export function SlotTitle({ slot, now }) {
    return (
        <div className="flex items-center gap-2">
            <span className="font-hand text-[24px] leading-[26px]">
                {formatClock24(slot.at)} · {slot.name}
            </span>
            {isJustWritten(slot.at, now) ? <JustNow /> : null}
            <span className="ml-auto flex shrink-0 items-center gap-1">
                <Avatar memberKey={slot.memberKey} size={20} />
                <span className="text-[12px] text-text-secondary">
                    {withJosa(nameOf(slot.memberKey), '이', '가')} 적음
                </span>
            </span>
        </div>
    );
}

/**
 * 적힌 칸 하나. 매일 콘텐츠는 그 카드(리액션 · 근거 칩 그대로)에 손글씨 제목 줄을 얹고,
 * 멤버 일상 글은 본문 + 「글 열기 ›」. fresh 면 분필로 쓰듯 드러납니다(MO5)
 */
export function ChalkSlot({ slot, now, fresh, board, onReact, onAskBirth, onOpenEvidence, onOpenMemberPost }) {
    const motion = fresh ? 'motion-safe:animate-chalk-write' : '';
    if (slot.type === 'daily') {
        return (
            <div className={`mt-2.5 ${motion}`}>
                <DailyItemCard
                    item={slot.item}
                    board={board}
                    onStartDay={() => {}}
                    onAskBirth={onAskBirth}
                    onReact={onReact}
                    onOpenEvidence={onOpenEvidence}
                    header={<SlotTitle slot={slot} now={now} />}
                />
            </div>
        );
    }
    return (
        <article
            aria-label={slot.name}
            className={`mt-2.5 flex flex-col gap-2 rounded-card border border-hairline bg-surface px-4 pt-3.5 pb-3 ${motion}`}
        >
            <SlotTitle slot={slot} now={now} />
            <p className="m-0 line-clamp-4 font-serif text-[16px] leading-[27px] whitespace-pre-wrap">
                {slot.post.body}
            </p>
            <button
                type="button"
                onClick={() => onOpenMemberPost(slot.post.memberPostsId)}
                className="min-h-touch self-start text-[13px] font-semibold text-brand"
            >
                글 열기 ›
            </button>
        </article>
    );
}

function plannedLine(slot) {
    return `${slot.time} · ${withJosa(nameOf(slot.memberKey), '이', '가')} ${slot.name} 적을 예정`;
}

/**
 * 오늘 남은 칸: **다음 한 칸만** 점선 윤곽, 나머지는 「오늘 더 적힐 것」으로 접어 둡니다(사용자 결정 2026-10-13, D54)
 */
export function PlannedSlots({ schedule }) {
    const [open, setOpen] = useState(false);
    if (!schedule?.length) {
        return null;
    }
    const [next, ...rest] = schedule;
    return (
        <section aria-label="오늘 남은 칸" className="mt-5 flex flex-col gap-2">
            <h3 className="m-0 text-[12px] font-semibold text-label-muted">오늘 남은 칸</h3>
            <p className="m-0 rounded-card border-[1.5px] border-dashed border-plan-outline px-4 py-3 font-hand text-[22px] leading-6 text-text-secondary">
                {plannedLine(next)}
            </p>
            {rest.length ? (
                <>
                    <button
                        type="button"
                        aria-expanded={open}
                        onClick={() => setOpen((value) => !value)}
                        className="flex min-h-touch items-center gap-1 self-start text-[13px] font-semibold text-text-secondary"
                    >
                        오늘 더 적힐 것
                        {open ? (
                            <ChevronDown size={16} strokeWidth={1.6} aria-hidden="true" />
                        ) : (
                            <ChevronRight size={16} strokeWidth={1.6} aria-hidden="true" />
                        )}
                    </button>
                    {open ? (
                        <ul className="m-0 flex list-none flex-col gap-1 p-0 pl-1">
                            {rest.map((slot) => (
                                <li key={slot.key} className="font-hand text-[22px] leading-6 text-text-secondary">
                                    {plannedLine(slot)}
                                </li>
                            ))}
                        </ul>
                    ) : null}
                </>
            ) : null}
        </section>
    );
}

/**
 * 지난 칠판: 날짜 줄(「10월 12일 월요일」 + 꺾쇠, 칸 수는 출석 숫자처럼 읽혀 뺌 — D54). 펼치면 그날 칸 요약(손글씨 제목 20 + 본문 한 줄)
 * @param {{ past: { date: string, label: string, count: number }[], open: Record<string, object[]|'loading'|'error'>,
 *   onToggle: (date: string) => void, onMore: (() => void)|null, loadingMore: boolean }} props
 */
export function PastBoards({ past, open, onToggle, onMore, loadingMore }) {
    if (!past?.length) {
        return null;
    }
    return (
        <section aria-label="지난 칠판" className="mt-6 flex flex-col">
            <h3 className="m-0 mb-1 text-[12px] font-semibold text-label-muted">지난 칠판</h3>
            {past.map((day) => {
                const state = open[day.date];
                const expanded = Boolean(state);
                return (
                    <div key={day.date} className="flex flex-col border-b border-hairline">
                        <button
                            type="button"
                            aria-expanded={expanded}
                            onClick={() => onToggle(day.date)}
                            className={`flex min-h-[52px] items-center justify-between text-left text-[15px] ${
                                expanded ? 'font-bold' : 'font-medium'
                            }`}
                        >
                            {day.label}
                            {expanded ? (
                                <ChevronDown
                                    size={18}
                                    strokeWidth={1.6}
                                    aria-hidden="true"
                                    className="text-label-muted"
                                />
                            ) : (
                                <ChevronRight
                                    size={18}
                                    strokeWidth={1.6}
                                    aria-hidden="true"
                                    className="text-label-muted"
                                />
                            )}
                        </button>
                        {state === 'loading' ? (
                            <p className="m-0 pb-3 text-caption text-text-secondary">그날 칠판을 펼치는 중이에요.</p>
                        ) : null}
                        {state === 'error' ? (
                            <p role="alert" className="m-0 pb-3 text-caption text-error">
                                그날 칠판을 불러오지 못했어요.
                            </p>
                        ) : null}
                        {Array.isArray(state) ? (
                            <div className="flex flex-col gap-2 pb-3">
                                {state.map((slot) => (
                                    <div
                                        key={slot.key}
                                        className="flex flex-col gap-0.5 rounded-[12px] bg-surface px-3.5 py-2.5"
                                    >
                                        <span className="font-hand text-[20px] leading-[22px]">
                                            {formatClock24(slot.at)} · {slot.name}
                                        </span>
                                        <span className="truncate text-[14px] text-text-secondary">
                                            {slotSummaryOf(slot)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        ) : null}
                    </div>
                );
            })}
            {onMore ? (
                <button
                    type="button"
                    onClick={onMore}
                    disabled={loadingMore}
                    className="mt-2 min-h-touch self-center text-[13px] font-semibold text-brand disabled:opacity-40"
                >
                    더 보기
                </button>
            ) : null}
        </section>
    );
}

/** 지난 칠판 칸 요약: 본문 첫 줄 */
export function slotSummaryOf(slot) {
    const text =
        slot.type === 'daily'
            ? (slot.item.payload?.headline ??
              slot.item.payload?.translation ??
              slot.item.payload?.body ??
              slot.item.body ??
              '')
            : slot.post.body;
    return String(text).split('\n')[0];
}
